import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faUser, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { parseSelection, resolvePropertiesForSelection } from '../markets/marketResolver';
import { usePropertyCodes } from '../markets/usePropertyCodes';
import { SelectionTypeahead } from './SelectionTypeahead';
import { CodeTypeahead } from './CodeTypeahead';
// Taller shells with more padding on mobile for touch; condensed on desktop.
// The submit button mirrors these heights (DaisyUI's btn min-height must be
// overridden explicitly or the button renders taller than the fields).
const fieldShell = 'flex items-center gap-2 rounded-lg border border-base-300 bg-base-100 h-12 px-3 lg:h-10 lg:px-2';
const toIsoDate = (date) => DateTime.fromJSDate(date).toISODate() ?? '';
// The search form. A single dropdown lets the salesperson pick a property, a
// city, or a region (options sourced from the IBE CMS config). It holds its own
// field state and emits a fully built request on submit; resolving the
// selection to properties and fanning out happens downstream.
export const SearchBar = ({ options, onSearch, config, busy }) => {
    const [selectionValue, setSelectionValue] = useState('');
    const [range, setRange] = useState([null, null]);
    const [adults, setAdults] = useState(2);
    const [code, setCode] = useState('');
    const [codeType, setCodeType] = useState('promo');
    // Resolve the current selection to its properties so their codes can be
    // fetched and offered as suggestions. Empty until a valid selection is made.
    const selection = parseSelection(selectionValue, options);
    const selectedProperties = config && selection ? resolvePropertiesForSelection(config, selection) : [];
    const suggestions = usePropertyCodes(selectedProperties);
    const codeSuggestions = codeType === 'promo' ? suggestions.promoCodes : suggestions.corporateCodes;
    // Switching the code type invalidates a code typed for the other type.
    const handleCodeTypeChange = (next) => {
        setCodeType(next);
        setCode('');
    };
    const [arrival, departure] = range;
    const canSubmit = Boolean(selectionValue && arrival && departure) && !busy;
    const handleSubmit = (event) => {
        event.preventDefault();
        if (!canSubmit || !selection || !arrival || !departure) {
            return;
        }
        const trimmedCode = code.trim();
        onSearch({
            selection,
            arrival: toIsoDate(arrival),
            departure: toIsoDate(departure),
            adults,
            ...(trimmedCode && codeType === 'promo' ? { promoCode: trimmedCode } : {}),
            ...(trimmedCode && codeType === 'corporate'
                ? { corporateCode: trimmedCode }
                : {})
        });
    };
    return (_jsx("form", { onSubmit: handleSubmit, "aria-label": "Offer search", className: "rounded-2xl border border-base-300 bg-duvet p-2 shadow-sm", children: _jsxs("div", { className: "flex flex-col gap-2 lg:flex-row lg:items-center", children: [_jsx(SelectionTypeahead, { options: options, value: selectionValue, onChange: setSelectionValue, className: "flex-1" }), _jsxs("label", { className: `${fieldShell} lg:w-64`, children: [_jsx(FontAwesomeIcon, { icon: faCalendar, className: "text-cabana" }), _jsx("span", { className: "sr-only", children: "Stay dates" }), _jsx(DatePicker, { selectsRange: true, startDate: arrival, endDate: departure, minDate: new Date(), dateFormat: "MMM d, yyyy", placeholderText: "Arrival \u2013 Departure", "aria-label": "Stay dates", className: "w-full bg-transparent text-sm outline-none", onChange: (dates) => setRange(dates) })] }), _jsxs("label", { className: fieldShell, children: [_jsx(FontAwesomeIcon, { icon: faUser, className: "text-cabana" }), _jsx("span", { className: "text-xs text-light-midnight", children: "Adults" }), _jsx("input", { type: "number", min: 1, "aria-label": "Adults", value: adults, onChange: e => setAdults(Math.max(1, Number(e.target.value) || 1)), className: "w-14 bg-transparent text-sm outline-none" })] }), _jsx(CodeTypeahead, { codeType: codeType, onCodeTypeChange: handleCodeTypeChange, value: code, onChange: setCode, suggestions: codeSuggestions, className: "flex-1" }), _jsxs("button", { type: "submit", disabled: !canSubmit, className: "btn btn-primary h-12 min-h-[3rem] px-4 text-sm lg:h-10 lg:min-h-[2.5rem]", children: [_jsx(FontAwesomeIcon, { icon: faMagnifyingGlass }), "Search offers"] })] }) }));
};
